import { useCallback, useEffect, useState } from 'react'
import { findSeat, type EventRecord } from '@romain130492/topchopsticks'
import { api } from '../lib/api'
import { getToken } from '../lib/storage'
import { showToast } from '../lib/toast'
import { useI18n } from './useI18n'

/**
 * Reserve / cancel, shared by the card and the detail page — the same bundle
 * the mini program passes around, so both surfaces behave identically.
 */
export function useEventBooking({ onChanged }: { onChanged?: () => void } = {}) {
  const { t } = useI18n()
  const [matches, setMatches] = useState<Array<{ id?: string; event_id: string; status: string }> | null>(null)
  const [busyEventId, setBusyEventId] = useState<string | null>(null)

  /**
   * A MONOTONIC counter, not a count of anything.
   *
   * This used to be `matches?.length || 0`, handed to AttendeeAvatars as its
   * refresh key — and a length is not a version, because it can return to a
   * value it already had.
   *
   * Cancelling is exactly that case. The backend does not delete the row, it
   * PATCHes the status to `cancelled`, so after join-then-cancel the array is
   * still length 1. The key never changed, the effect never re-ran, and the
   * attendee stack kept showing a face for a seat that no longer existed until
   * the page was reloaded. The BUTTON updated correctly the whole time —
   * `seatFor` filters by status — which is what made it look cosmetic.
   *
   * Bumped on every completed load, so a pull-to-refresh re-reads the faces too.
   */
  const [version, setVersion] = useState(0)

  const load = useCallback(async () => {
    if (!getToken()) { setMatches([]); setVersion((n) => n + 1); return }
    try {
      // A bare array — see the note in the shared client. `result.matches`
      // was always undefined, so nobody ever appeared booked.
      const result = await api.getMyEventMatches()
      setMatches((Array.isArray(result) ? result : []) as never)
    } catch { setMatches([]) }
    setVersion((n) => n + 1)
  }, [])

  useEffect(() => { load() }, [load])

  const seatFor = (eventId: string) => findSeat(matches || [], eventId)

  const reserve = async (event: EventRecord) => {
    setBusyEventId(event.id)
    try {
      await api.joinEvent(event.id)
      showToast({ title: t('ev.seatConfirmed'), icon: 'success' })
      await load()
      onChanged && onChanged()
    } catch (error) {
      showToast({ title: (error as { message?: string })?.message || t('ev.reserveFailed'), icon: 'none' })
    } finally { setBusyEventId(null) }
  }

  const cancel = async (event: EventRecord) => {
    const seat = seatFor(event.id) as { id?: string } | null
    if (!seat?.id) return
    if (!window.confirm(t('ev.cancelConfirm'))) return
    setBusyEventId(event.id)
    try {
      await api.cancelSeat(seat.id)
      showToast({ title: t('ev.cancelled'), icon: 'success' })
      await load()
      onChanged && onChanged()
    } catch (error) {
      showToast({ title: (error as { message?: string })?.message || t('ev.cancelFailed'), icon: 'none' })
    } finally { setBusyEventId(null) }
  }

  return { matches, seatFor, reserve, cancel, busyEventId, ready: matches !== null, reload: load, version }
}
